/**
 * TRX (Transactional) experiment — parent ticket TM-1296.
 *
 * Lands a paused customer directly on /menu for the earliest available
 * delivery date and slot, bypassing the /delivery-details step. Consumed
 * by TRX-experiment surfaces: the Gousto Anytime modal on MyGousto, the
 * Order Confirmation page, and the direct links to /menu from marketing comms (TM-1571).
 *
 * Remove or fold into the standard flow once the TRX experiment ships.
 */
import type Immutable from 'immutable'
import moment from 'moment'
import type { RootStateOrAny } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { basketDateChange, basketReset, basketSlotChange, getMenuCutoffUntil } from 'actions/basket'
import { boxSummaryDeliveryDaysLoad } from 'actions/boxSummary'
import { redirect } from 'actions/redirect'
import { subscriptionLoadData } from 'actions/subscription'
import userActions from 'actions/user'
import { client as clientRoutes } from 'config/routes'
import { resolveSlotIdForDay } from 'routes/Menu/selectors/resolveSlotIdForDay'
import { getLandingDay } from 'utils/deliveries'
import logger from 'utils/logger'

type GetState = () => RootStateOrAny

type HydrateOneOffOrderOptions = {
  // Core ids from a /menu deeplink (TM-1571). When omitted, the next available
  // delivery date and slot are chosen.
  dayId?: string
  slotId?: string
}

type ResolvedSlot = { date: string; slotId: string }

// Whether the customer already has an order on the given calendar day. Compared
// at day granularity because delivery dates can carry a time component.
const dateHasExistingOrder = (
  userOrders: Immutable.List<Immutable.Map<string, any>> | undefined,
  date: string,
): boolean =>
  !!userOrders?.some((order) => moment(date).isSame(moment(order?.get('deliveryDate')), 'day'))

// Resolve the core day/slot ids supplied on a /menu deeplink against the
// loaded transactional delivery days. Returns undefined when the day or slot
// can't be found, or when the customer already has an order on that date —
// the caller treats that like control and redirects to /delivery-details.
const resolveSlotFromIds = (
  state: RootStateOrAny,
  dayId: string,
  slotId?: string,
): ResolvedSlot | undefined => {
  // boxSummaryDeliveryDays is an Immutable.Map keyed by delivery date (see the
  // boxSummary reducer's transformDaysResponse). Map.find iterates the values.
  const deliveryDays = state.boxSummaryDeliveryDays as
    | Immutable.Map<string, Immutable.Map<string, any>>
    | undefined

  const day = deliveryDays?.find((deliveryDay) => deliveryDay?.get('coreDayId') === dayId)
  // Bounce to delivery options if we can't deliver on this day
  if (!day || day.get('alternateDeliveryDay')) {
    return undefined
  }

  const date = day.get('date') as string

  // The customer already has an order on this date — not a usable landing day.
  const userOrders = state.user.get('orders') as Immutable.List<Immutable.Map<string, any>>
  if (dateHasExistingOrder(userOrders, date)) {
    return undefined
  }

  // Pick a bookable slot, rejecting slots disabled for this customer exactly as
  // getLandingDay does. An explicit slot id must match a bookable slot on the day
  // (a stale link otherwise resolves to undefined and bounces to /delivery-details);
  // without one, fall back to the day's default (or first) bookable slot.
  const resolvedSlotId = resolveSlotIdForDay(state, day, { coreSlotId: slotId })

  if (!resolvedSlotId) {
    return undefined
  }

  return { date, slotId: resolvedSlotId }
}

// Pick the next available landing day/slot using the same helper delivery-details
// uses, so the two surfaces stay in sync. cantLandOnOrderDate keeps the one-off flow
// off days where the customer already has an order.
const resolveLandingDay = (state: RootStateOrAny): ResolvedSlot | undefined => {
  const { date, slotId } = getLandingDay(state, {
    useCurrentSlot: true,
    useBasketDate: false,
    cantLandOnOrderDate: true,
  })

  if (!date || !slotId) {
    return undefined
  }

  // getLandingDay's own order filters compare at millisecond granularity, so an
  // order stored with a time can slip through; re-check at day granularity and
  // bounce to /delivery-details rather than hydrate onto a day with an order.
  const userOrders = state.user?.get('orders') as Immutable.List<Immutable.Map<string, any>>
  if (dateHasExistingOrder(userOrders, date)) {
    return undefined
  }

  return { date, slotId }
}

// Hydrates basket state for a one-off (transactional) order without redirecting.
// Loads user data, the default address and box size, and the transactional
// delivery days, then selects a delivery date and slot. Returns whether a usable
// date/slot was found so the caller can decide where to navigate.
export const hydrateOneOffOrder =
  (options: HydrateOneOffOrderOptions = {}) =>
  async (
    dispatch: ThunkDispatch<RootStateOrAny, unknown, AnyAction>,
    getState: GetState,
  ): Promise<{ ok: boolean }> => {
    // Start with a clean basket so prior recipes can't leak into the one-off flow.
    dispatch(basketReset())

    try {
      // Mirrors the data fetching 'delivery-details' runs on mount. This also applies
      // the customer's default shipping address (userFetchShippingAddresses).
      await Promise.all([
        dispatch(userActions.userLoadData()),
        dispatch(userActions.userFetchShippingAddresses()),
        dispatch(userActions.userLoadOrders(true)),
      ])

      // userLoadData dispatches subscriptionLoadData without awaiting it, so await
      // it explicitly here: the saved box size (numPortions) must be applied before
      // the menu is fetched, otherwise the menu loads against the reset default.
      await dispatch(subscriptionLoadData())

      // 'transactional' is the order type that exposes one-off slots to unsubscribed customers.
      const cutoffUntil = await getMenuCutoffUntil()
      await dispatch(boxSummaryDeliveryDaysLoad(undefined, cutoffUntil, 'transactional'))

      // Honour the day/slot supplied on the deeplink; otherwise pick the next
      // available landing day.
      const resolved = options.dayId
        ? resolveSlotFromIds(getState(), options.dayId, options.slotId)
        : resolveLandingDay(getState())

      if (!resolved) {
        return { ok: false }
      }

      dispatch(basketDateChange(resolved.date))
      dispatch(basketSlotChange(resolved.slotId))

      return { ok: true }
    } catch (err: any) {
      logger.error(err)

      return { ok: false }
    }
  }

export const startOneOffOrder =
  () =>
  async (
    dispatch: ThunkDispatch<RootStateOrAny, unknown, AnyAction>,
    getState: GetState,
  ): Promise<void> => {
    const { ok } = await hydrateOneOffOrder()(dispatch, getState)

    dispatch(redirect(ok ? clientRoutes.menu : clientRoutes.deliveryDetails))
  }
